import { useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import {
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import GoogleMapReact from 'google-map-react';
import { MapPin, MapIcon, Home } from 'lucide-react';
import { useAppSelector, useUserProfile } from '@/hooks';
import { RootState } from '@/store/store';
import useOrganizations from '@/features/organizations/useOrganizations';
import { v4 } from 'uuid';
import { Location as ILocation } from '@/types/user';
import { CheckoutSharedProps } from '@/pages/checkout/CheckoutPage';
import pick from 'lodash/pick';

export const LocationFormSchema = Yup.object().shape({
  neighborhood: Yup.string().required(
    'Campo requerido'
  ),
  house: Yup.string(),
  address: Yup.string().required(
    'Campo requerido'
  ),
});

export type ILocationForm = ILocation;

export const locationFormInitialValues: ILocationForm = {
  id: '',
  neighborhood: '',
  address: '',
  house: '',
  latitude: undefined,
  longitude: undefined,
};

export const locationFormFields = Object.keys(locationFormInitialValues);

export const onSubmitLocationForm = async (values: ILocationForm, sharedProps: CheckoutSharedProps) => {
  const { activeUser } = sharedProps;

  const draft = pick(values, locationFormFields);

  if (!draft?.id) draft.id = v4();

  if (activeUser?.isAnonymous) {
    const ls = localStorage.getItem('user');
    const lsUser = JSON.parse(ls ?? '{}');
    const updatedUser = JSON.stringify({ ...lsUser, location: pick(values, locationFormFields) });
    localStorage.setItem('user', updatedUser);
  }
};

const LocationForm = () => {
  const { activeUser } = useUserProfile();
  const { currentOrganization } = useOrganizations();
  const order = useAppSelector((state: RootState) => state.order);
  const formik = useFormikContext<ILocationForm>();

  const INITIAL_LOCATION = {
    lat: currentOrganization?.latitude ?? 12.115,
    lng: currentOrganization?.longitude ?? -86.2362,
  };

  const loadMap = (map: any, maps: any) => {
    const position = {
      lat: formik.values?.latitude ?? INITIAL_LOCATION.lat,
      lng: formik.values?.longitude ?? INITIAL_LOCATION.lng,
    };

    const marker = new maps.Marker({
      position,
      map,
      draggable: true,
    });

    marker.addListener('dragend', () => {
      const newLatitude = marker.getPosition().lat();
      const newLongitude = marker.getPosition().lng();

      formik.setFieldValue('latitude', newLatitude);
      formik.setFieldValue('longitude', newLongitude);
    });
  };

  const loadDeliveryLocation = useCallback(() => {
    let locations: ILocation[] = [];

    if (activeUser) {
      if (activeUser?.isAnonymous && activeUser?.location) {
        locations.push(activeUser.location);
      } else if (activeUser?.locations) {
        locations = [...activeUser.locations];
      }
    }

    if (!locations?.length) return;

    const selectedLocation = locations.find(
      (l) => l.id === order.deliveryLocation
    );

    if (selectedLocation) {
      formik.setValues({
        ...formik.values,
        ...selectedLocation,
      });
    } else {
      formik.setValues({
        ...formik.values,
        ...locations[0],
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser, order.deliveryLocation]);

  useEffect(() => {
    loadDeliveryLocation();
  }, [loadDeliveryLocation]);

  return (
      <Stack spacing={2}>
        <TextField
          name="house"
          label="No. Casa"
          value={formik.values.house}
          error={Boolean(formik.errors.house)}
          onBlur={formik.handleBlur}
          helperText={formik.errors.house}
          onChange={formik.handleChange}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="start">
                  <Home />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          name="neighborhood"
          label="Barrio"
          value={formik.values.neighborhood}
          error={Boolean(formik.errors.neighborhood)}
          onBlur={formik.handleBlur}
          helperText={formik.errors.neighborhood}
          onChange={formik.handleChange}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="start">
                  <MapPin />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          name="address"
          label="Dirección/Instrucciones"
          value={formik.values.address}
          error={Boolean(formik.errors.address)}
          onBlur={formik.handleBlur}
          helperText={formik.errors.address}
          multiline
          rows={4}
          onChange={formik.handleChange}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="start">
                  <MapIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <Stack style={{ height: 300, width: '100%' }}>
          <GoogleMapReact
            defaultCenter={{
              lat: formik.values.latitude ?? INITIAL_LOCATION.lat,
              lng: formik.values.longitude ?? INITIAL_LOCATION.lng,
            }}
            onGoogleApiLoaded={({ map, maps }) => loadMap(map, maps)}
            defaultZoom={13}
            bootstrapURLKeys={{
              key: import.meta.env.VITE_FIREBASE_API_KEY,
            }}
            yesIWantToUseGoogleMapApiInternals
            options={{
              mapTypeId: 'roadmap',
            }}
          />
        </Stack>
    </Stack>
  );
};

export default LocationForm;
